<div fxLayout="column" class="cipo cipo--height--full">
    <!-- ── Header ── -->
    <div class="p-x p-y-sm cipo-bg--primary-100" fxLayout fxLayoutAlign="space-between center">
        <div fxLayout fxLayoutAlign="start center" fxLayoutGap="8px">
            <mat-icon class="material-symbols-outlined" color="primary">auto_awesome</mat-icon>
            <span class="cipo-text--bold-md">{{ "CIPO_AI.TITLE" | translate }}</span>
        </div>
        <div fxLayout>
            <button
                mat-icon-button
                [matTooltip]="'CIPO_AI.NEW_THREAD' | translate"
                [attr.aria-label]="'CIPO_AI.NEW_THREAD' | translate"
                [disabled]="aiStore.loading()"
                (click)="aiStore.newThread()">
                <mat-icon aria-hidden="true">add</mat-icon>
            </button>
            <button
                mat-icon-button
                [matTooltip]="'CIPO_AI.ALL_THREADS' | translate"
                [attr.aria-label]="'CIPO_AI.ALL_THREADS' | translate"
                [disabled]="aiStore.loading()"
                (click)="aiStore.setViewMode('threads')">
                <mat-icon aria-hidden="true">forum</mat-icon>
            </button>
            <button
                mat-icon-button
                [matTooltip]="'CIPO_AI.CLOSE' | translate"
                [attr.aria-label]="'CIPO_AI.CLOSE' | translate"
                (click)="aiStore.toggleOpen()">
                <mat-icon aria-hidden="true">close</mat-icon>
            </button>
        </div>
    </div>

    <mat-divider />

    <!-- ── Threads view ── -->
    @if (aiStore.viewMode() === "threads") {
        <div fxFlex class="cipo--overflow-y--auto" data-testid="cipo-ai-container">
            @if (aiStore.loading()) {
                <div fxLayout="column" data-testid="cipo-ai-loading">
                    @for (item of [65, 80, 50, 75]; track $index) {
                        <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="16px" class="p-x p-y-sm">
                            <ngx-skeleton-loader
                                [theme]="{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '6px',
                                    flexShrink: '0',
                                    marginBottom: '0',
                                }" />
                            <div fxFlex fxLayout="column" fxLayoutGap="5px">
                                <ngx-skeleton-loader
                                    [theme]="{
                                        width: item + '%',
                                        height: '13px',
                                        borderRadius: '6px',
                                        marginBottom: '0',
                                    }" />
                                <ngx-skeleton-loader
                                    [theme]="{
                                        width: '38%',
                                        height: '11px',
                                        borderRadius: '6px',
                                        marginBottom: '0',
                                    }" />
                            </div>
                            <ngx-skeleton-loader
                                [theme]="{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    flexShrink: '0',
                                    marginBottom: '0',
                                }" />
                        </div>
                        @if ($index < 3) {
                            <mat-divider />
                        }
                    }
                </div>
            } @else if (aiStore.threads().length === 0) {
                <div
                    fxLayout="column"
                    fxLayoutAlign="center center"
                    fxLayoutGap="12px"
                    class="m-t-xl p-a-md"
                    data-testid="cipo-ai-no-threads">
                    <mat-icon
                        class="material-symbols-outlined cipo-text--black-400"
                        style="font-size: 48px; width: 48px; height: 48px">
                        chat_bubble
                    </mat-icon>
                    <span class="cipo-text--black-600">{{ "CIPO_AI.THREADS.EMPTY" | translate }}</span>
                    <span class="cipo-text--black-400">{{ "CIPO_AI.THREADS.EMPTY_HINT" | translate }}</span>
                </div>
            } @else {
                <mat-nav-list disableRipple data-testid="cipo-ai-threads-list">
                    @for (thread of aiStore.threads(); track thread.threadId) {
                        <mat-list-item
                            class="cipo--cursor--pointer"
                            data-testid="cipo-ai-thread-item"
                            (click)="aiStore.selectThread(thread.threadId)">
                            <mat-icon matListItemIcon class="material-symbols-outlined cipo-text--black-400">
                                chat
                            </mat-icon>
                            <div matListItemTitle class="cipo--text--ellipsis">
                                @let msgs = thread.messages ?? [];
                                {{
                                    msgs.length && msgs[0].items.length
                                        ? (msgs[0].items[0].text ?? ("CIPO_AI.THREADS.UNTITLED" | translate))
                                        : ("CIPO_AI.THREADS.UNTITLED" | translate)
                                }}
                            </div>
                            <div matListItemLine class="cipo-text--black-400">
                                {{ thread.createdAt | itvDateTime }}
                            </div>
                            <button
                                mat-icon-button
                                matListItemMeta
                                [matTooltip]="'CIPO_AI.DELETE_THREAD' | translate"
                                [attr.aria-label]="'CIPO_AI.DELETE_THREAD' | translate"
                                [disabled]="aiStore.loading()"
                                (click)="aiStore.deleteThread(thread.threadId); $event.stopPropagation()">
                                <mat-icon class="cipo-text--black-400" aria-hidden="true">delete_outline</mat-icon>
                            </button>
                        </mat-list-item>
                        @if ($index < aiStore.threads().length - 1) {
                            <mat-divider />
                        }
                    }
                </mat-nav-list>
            }
        </div>
    } @else {
        <!-- ── Chat view ── -->
        @let thread = aiStore.thread();
        @let messages = thread?.messages ?? [];

        <div fxFlex class="cipo--overflow-y--auto p-x p-y-sm" #responseContainer data-testid="cipo-ai-response">
            @if (!thread && !aiStore.loading()) {
                <div fxLayout="column" fxLayoutAlign="center center" fxLayoutGap="8px" class="m-t-xl">
                    <mat-icon
                        class="material-symbols-outlined cipo-text--black-400"
                        style="font-size: 48px; width: 48px; height: 48px">
                        auto_awesome
                    </mat-icon>
                    <span class="cipo-text--black-600">{{ "CIPO_AI.CHAT.WELCOME" | translate }}</span>
                    <span class="cipo-text--black-400">{{ "CIPO_AI.CHAT.WELCOME_HINT" | translate }}</span>
                </div>
            }

            @for (message of messages; track $index; let messageIndex = $index) {
                @let isUser = message.role === "user";

                <div
                    fxLayout="column"
                    [fxLayoutAlign]="isUser ? 'end' : 'start'"
                    class="m-b-md"
                    [attr.data-testid]="'cipo-ai-message cipo-ai-message--' + message.role">
                    @if (!isUser) {
                        @let steps = aiStore.meaningfulReasoningSteps(message);
                        @if (steps.length > 0) {
                            <div fxLayout="row" fxLayoutGap="8px" class="m-b-sm" data-testid="cipo-ai-reasoning-trace">
                                <mat-icon class="material-symbols-outlined cipo-text--black-400" fxFlex="none">
                                    assistant
                                </mat-icon>
                                <div
                                    fxLayout="column"
                                    fxLayoutGap="4px"
                                    class="cipo-ai__trace-panel cipo--width--full p-a-sm cipo--border-radius--sm"
                                    fxFlex>
                                    <span class="cipo-text--sm cipo-text--black-400">
                                        {{
                                            (steps.length === 1
                                                ? "CIPO_AI.REASONING.USED_STEPS_SINGULAR"
                                                : "CIPO_AI.REASONING.USED_STEPS_PLURAL"
                                            ) | translate: { count: steps.length }
                                        }}
                                    </span>
                                    @for (step of steps; track $index) {
                                        <ng-container *ngTemplateOutlet="completedStep; context: { step: step }" />
                                    }
                                </div>
                            </div>
                        }
                    }

                    <div fxLayout="row" [fxLayoutAlign]="isUser ? 'end start' : 'start start'" fxLayoutGap="8px">
                        @if (!isUser) {
                            <mat-icon class="material-symbols-outlined cipo-text--black-400" fxFlex="none">
                                assistant
                            </mat-icon>
                        }

                        <div
                            class="p-x-sm p-y-sm cipo--border-radius--sm"
                            [class.cipo-bg--primary-100]="!isUser"
                            [class.cipo-bg--primary-300]="isUser">
                            @for (item of message.items; track $index; let itemIndex = $index) {
                                @if (isUser) {
                                    @for (line of (item.text ?? "").split("\n"); track $index) {
                                        <p class="m-a-0">{{ line }}</p>
                                    }
                                } @else if (isDownloadItem(item)) {
                                    @let downloadState = getDownloadState(messageIndex, itemIndex);
                                    <div
                                        fxLayout="column"
                                        fxLayoutGap="6px"
                                        class="m-t-xs m-b-xs"
                                        data-testid="cipo-ai-download-container">
                                        <div class="cipo-ai__download-split">
                                            <button
                                                mat-raised-button
                                                color="primary"
                                                type="button"
                                                class="cipo-ai__download-main-btn"
                                                data-testid="cipo-ai-download-button"
                                                [disabled]="downloadState.downloading"
                                                (click)="
                                                    downloadGeneratedFile(
                                                        item,
                                                        messageIndex,
                                                        itemIndex,
                                                        undefined,
                                                        $event
                                                    )
                                                ">
                                                <mat-icon class="material-symbols-outlined" aria-hidden="true">
                                                    download
                                                </mat-icon>
                                                <span>
                                                    {{
                                                        "CIPO_AI.CHAT.DOWNLOAD_FILE"
                                                            | translate: { fileName: getDownloadFileName(item) }
                                                    }}
                                                </span>
                                            </button>
                                            <button
                                                mat-raised-button
                                                color="primary"
                                                type="button"
                                                class="cipo-ai__download-menu-btn"
                                                data-testid="cipo-ai-download-menu-trigger"
                                                [disabled]="downloadState.downloading"
                                                [matMenuTriggerFor]="downloadFormatMenu"
                                                [matTooltip]="'CIPO_AI.CHAT.DOWNLOAD_OTHER_FORMATS' | translate"
                                                [attr.aria-label]="'a11y.cipoAi.selectDownloadFormat' | translate">
                                                <mat-icon class="material-symbols-outlined" aria-hidden="true">
                                                    keyboard_arrow_down
                                                </mat-icon>
                                            </button>
                                            <mat-menu #downloadFormatMenu="matMenu" xPosition="before" class="cipo">
                                                @for (option of exportFormatOptions; track option.format) {
                                                    <button
                                                        mat-menu-item
                                                        type="button"
                                                        [attr.data-testid]="'cipo-ai-download-format-' + option.format"
                                                        (click)="
                                                            downloadGeneratedFile(
                                                                item,
                                                                messageIndex,
                                                                itemIndex,
                                                                option.format,
                                                                $event
                                                            )
                                                        ">
                                                        <mat-icon class="material-symbols-outlined" aria-hidden="true">
                                                            {{ option.icon }}
                                                        </mat-icon>
                                                        <span>{{ option.labelKey | translate }}</span>
                                                    </button>
                                                }
                                            </mat-menu>
                                        </div>

                                        @if (downloadState.error) {
                                            <div
                                                fxLayout="row"
                                                fxLayoutAlign="space-between center"
                                                fxLayoutGap="8px"
                                                class="p-x-sm p-y-xs cipo--border-radius--sm"
                                                role="alert"
                                                data-testid="cipo-ai-download-error">
                                                <span class="cipo-text--sm cipo-text--error">
                                                    {{ "CIPO_AI.CHAT.DOWNLOAD_ERROR" | translate }}
                                                </span>
                                                <button
                                                    mat-stroked-button
                                                    color="primary"
                                                    type="button"
                                                    data-testid="cipo-ai-download-retry"
                                                    (click)="
                                                        retryDownloadGeneratedFile(
                                                            item,
                                                            messageIndex,
                                                            itemIndex,
                                                            $event
                                                        )
                                                    ">
                                                    {{ "CIPO_AI.CHAT.DOWNLOAD_RETRY" | translate }}
                                                </button>
                                            </div>
                                        }
                                    </div>
                                } @else if (isFileReference(item) && isFirstStandaloneFileReference(message, item)) {
                                    <div class="cipo-ai__file-references" data-testid="cipo-ai-file-references">
                                        <p class="cipo-ai__file-references-header">
                                            {{ "CIPO_AI.CHAT.FILE_REFERENCES" | translate }}
                                        </p>
                                        <ul class="cipo-ai__file-references-list">
                                            @for (fileRef of getStandaloneFileReferences(message); track $index) {
                                                <li>
                                                    <a
                                                        class="cipo-ai__file-reference cipo-ai__file-chip"
                                                        href="#"
                                                        (click)="openReferencedFile(fileRef, $event)">
                                                        <mat-icon
                                                            class="material-symbols-outlined cipo-ai__file-chip-icon"
                                                            aria-hidden="true">
                                                            description
                                                        </mat-icon>
                                                        <span class="cipo-ai__file-chip-label">{{ fileRef.text }}</span>
                                                        <mat-icon
                                                            class="material-symbols-outlined cipo-ai__file-chip-external"
                                                            aria-hidden="true">
                                                            open_in_new
                                                        </mat-icon>
                                                    </a>
                                                </li>
                                            }
                                        </ul>
                                    </div>
                                } @else if (isFileReference(item)) {
                                    <!-- Grouped under the first file reference item above -->
                                } @else if (isUrlReference(item) && shouldRenderStandaloneReference(message, item)) {
                                    <a
                                        class="cipo-ai__file-reference"
                                        href="#"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        (click)="openExternalReference(item, $event)">
                                        {{ item.text }}
                                    </a>
                                } @else if (isFollowUp(item)) {
                                    <button
                                        class="cipo-ai__followup"
                                        mat-stroked-button
                                        color="primary"
                                        (click)="runFollowUp(item, $event)">
                                        {{ item.text }}
                                    </button>
                                } @else if (item.itemType === itemTypes.Text || item.itemType === undefined) {
                                    <div
                                        class="cipo-ai__markdown"
                                        markdown
                                        [data]="item.text"
                                        tabindex="-1"
                                        (keydown)="handleAssistantMarkdownKeydown($event)"
                                        (click)="handleAssistantMarkdownClick($event, message)"></div>
                                }
                            }
                        </div>

                        @if (isUser) {
                            <mat-icon class="material-symbols-outlined cipo-text--black-400" fxFlex="none">
                                person
                            </mat-icon>
                        }
                    </div>
                </div>
            }

            @if (aiStore.loading()) {
                <div fxLayout="row" fxLayoutGap="8px" class="m-b-md" data-testid="cipo-ai-loading">
                    <mat-icon class="material-symbols-outlined cipo-text--black-400" fxFlex="none" aria-hidden="true">
                        assistant
                    </mat-icon>
                    <div fxLayout="column" fxLayoutGap="8px" fxFlex>
                        @if (aiStore.reasoningSteps().length > 0) {
                            <div fxLayout="column" fxLayoutGap="4px">
                                @for (step of aiStore.reasoningSteps(); track $index) {
                                    @if ($last) {
                                        <div
                                            fxLayout="row"
                                            fxLayoutAlign="start center"
                                            fxLayoutGap="6px"
                                            class="cipo-text--sm"
                                            [class.cipo-text--primary]="step.kind !== 'error'"
                                            [class.cipo-text--error]="step.kind === 'error'">
                                            @if (step.kind === "error") {
                                                <mat-icon
                                                    class="material-symbols-outlined"
                                                    style="font-size: 14px; width: 14px; height: 14px"
                                                    aria-hidden="true">
                                                    error_outline
                                                </mat-icon>
                                            } @else {
                                                <mat-icon
                                                    class="material-symbols-outlined cipo-ai__reasoning-spin"
                                                    style="font-size: 14px; width: 14px; height: 14px"
                                                    aria-hidden="true">
                                                    sync
                                                </mat-icon>
                                            }
                                            <span>{{ step.label }}</span>
                                        </div>
                                    } @else {
                                        <ng-container *ngTemplateOutlet="completedStep; context: { step: step }" />
                                    }
                                }
                            </div>
                        } @else {
                            <div
                                class="p-x-sm p-y-sm cipo--border-radius--sm"
                                fxLayout="column"
                                fxLayoutGap="6px"
                                style="min-width: 140px">
                                <ngx-skeleton-loader
                                    [theme]="{
                                        width: '120px',
                                        height: '12px',
                                        borderRadius: '6px',
                                        marginBottom: '0',
                                    }" />
                                <ngx-skeleton-loader
                                    [theme]="{
                                        width: '80px',
                                        height: '12px',
                                        borderRadius: '6px',
                                        marginBottom: '0',
                                    }" />
                            </div>
                        }

                        @if (aiStore.softTimeoutPromptVisible()) {
                            <div
                                fxLayout="column"
                                fxLayoutGap="8px"
                                class="p-x-sm p-y-sm cipo--border-radius--sm cipo-bg--primary-100"
                                role="status"
                                aria-live="polite"
                                data-testid="cipo-ai-soft-timeout-prompt">
                                <span class="cipo-text--sm">
                                    {{
                                        "CIPO_AI.CHAT.SOFT_TIMEOUT_MESSAGE"
                                            | translate: { seconds: aiStore.elapsedTimeoutSeconds() }
                                    }}
                                </span>
                                <div fxLayout="row" fxLayoutGap="8px" fxLayoutAlign="start center">
                                    <button
                                        mat-stroked-button
                                        color="primary"
                                        type="button"
                                        data-testid="cipo-ai-keep-waiting"
                                        (click)="aiStore.keepWaiting()">
                                        {{ "CIPO_AI.CHAT.KEEP_WAITING" | translate }}
                                    </button>
                                    <button
                                        mat-stroked-button
                                        color="warn"
                                        type="button"
                                        data-testid="cipo-ai-stop-request"
                                        [disabled]="!aiStore.canStopRequest()"
                                        (click)="aiStore.stopRequest()">
                                        {{ "CIPO_AI.CHAT.STOP_REQUEST" | translate }}
                                    </button>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            }
        </div>

        <mat-divider />

        <!-- ── Input area ── -->
        <div class="p-a-sm">
            <mat-checkbox class="m-b-sm" [formControl]="restrictToCurrentContextControl">
                {{ "CIPO_AI.CHAT.USE_CURRENT_CONTEXT" | translate }}
            </mat-checkbox>

            <mat-form-field class="cipo-input cipo--width--full" appearance="outline">
                <textarea
                    matInput
                    #searchInput
                    [formControl]="searchControl"
                    cdkTextareaAutosize
                    cdkAutosizeMinRows="1"
                    cdkAutosizeMaxRows="6"
                    [placeholder]="
                        (thread?.threadId ? 'CIPO_AI.CHAT.PLACEHOLDER_FOLLOWUP' : 'CIPO_AI.CHAT.PLACEHOLDER_NEW')
                            | translate
                    "
                    data-testid="cipo-ai-search"
                    (keydown.enter)="$event.preventDefault(); search()"></textarea>
                @if (aiStore.loading()) {
                    <button
                        matSuffix
                        mat-icon-button
                        color="warn"
                        type="button"
                        data-testid="cipo-ai-stop-button"
                        [disabled]="!aiStore.canStopRequest()"
                        (click)="aiStore.stopRequest()"
                        [matTooltip]="'CIPO_AI.CHAT.STOP_REQUEST' | translate"
                        [attr.aria-label]="'CIPO_AI.CHAT.STOP_REQUEST' | translate">
                        <mat-icon aria-hidden="true">stop_circle</mat-icon>
                    </button>
                } @else {
                    <button
                        matSuffix
                        mat-icon-button
                        color="primary"
                        type="button"
                        [disabled]="!searchControl.value"
                        (click)="search()"
                        [matTooltip]="'CIPO_AI.SEND' | translate"
                        [attr.aria-label]="'CIPO_AI.SEND' | translate">
                        <mat-icon aria-hidden="true">send</mat-icon>
                    </button>
                }
            </mat-form-field>

            <div
                class="p-x-xs cipo-text--label cipo-text--black-400 cipo--text--pre-line"
                [innerHTML]="'CIPO_AI.CHAT.DISCLAIMER' | translate | safeHtml"></div>
        </div>
    }
</div>

<!-- ── Shared reasoning step icon+label row (completed style, used by the post-response trace) ── -->
<ng-template #completedStep let-step="step">
    <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="6px" class="cipo-text--sm cipo-text--black-400">
        @if (step.kind === "reasoning") {
            <mat-icon
                class="material-symbols-outlined"
                style="font-size: 14px; width: 14px; height: 14px"
                aria-hidden="true">
                psychology
            </mat-icon>
        } @else if (step.kind === "error") {
            <mat-icon
                class="material-symbols-outlined"
                style="font-size: 14px; width: 14px; height: 14px"
                aria-hidden="true">
                error_outline
            </mat-icon>
        } @else {
            <mat-icon
                class="material-symbols-outlined"
                style="font-size: 14px; width: 14px; height: 14px"
                aria-hidden="true">
                check
            </mat-icon>
        }
        <span>{{ step.label }}</span>
    </div>
</ng-template>
